// Wire format for the routine-pack JSON import. Versioned envelope; mirrors
// the single-routine URL share (see features/share/types.ts) so a future
// "export this routine to JSON" button can reuse the shape. Multi-form
// `routines: [...]` and single-form `routine: {...}` are both accepted —
// `toRoutineDrafts` collapses them.
//
// Strict validation: every field type-checked, no silent coercion. Each
// rejection branch throws RoutinePackImportError with a Finnish message
// suitable for direct rendering in the settings hub.

export interface RoutineDraftWire {
  name: string;
  emoji: string;
  tasks: Array<{ name: string; emoji: string; tag: string | null }>;
}

// Discriminated union: a pack carries EITHER the array form OR the single
// form, never both and never neither. parseRoutinePack enforces this at
// runtime; expressing it in the type lets toRoutineDrafts be exhaustive
// without a dead fallback branch.
export type RoutinePackV1 =
  | { v: 1; routines: RoutineDraftWire[] }
  | { v: 1; routine: RoutineDraftWire };

export interface RoutineDraft {
  name: string;
  emoji: string;
  tasks: Array<{ name: string; emoji: string; tag: string | null }>;
}

export class RoutinePackImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoutinePackImportError';
  }
}

// Browsers tolerate huge files, but a multi-MB paste through this picker
// would only be an accident or attack. 256 KB is well past anything
// hand-authored or Claude-generated. Measured as UTF-8 bytes (not JS
// string length, which is UTF-16 code units) so the user-facing "kt"
// claim matches the on-disk size.
const MAX_RAW_BYTES = 256 * 1024;
const MAX_ROUTINES = 50;

const utf8Encoder = new TextEncoder();

export function parseRoutinePack(raw: string): RoutinePackV1 {
  if (utf8Encoder.encode(raw).length > MAX_RAW_BYTES) {
    throw new RoutinePackImportError(
      'Tiedosto on liian iso (yli 256 kt). Tarkista että tuot oikean tiedoston.',
    );
  }

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new RoutinePackImportError('Tiedosto ei ole kelvollista JSONia.');
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new RoutinePackImportError('Tiedosto ei ole kelvollinen rutiinipaketti.');
  }

  const obj = data as Record<string, unknown>;

  if (!('v' in obj)) {
    throw new RoutinePackImportError("Tiedostosta puuttuu kenttä 'v'.");
  }
  if (obj.v !== 1) {
    throw new RoutinePackImportError('Tämä tiedosto on uudemmasta versiosta — päivitä sovellus.');
  }

  const hasRoutines = 'routines' in obj;
  const hasRoutine = 'routine' in obj;
  if (hasRoutines && hasRoutine) {
    throw new RoutinePackImportError(
      "Tiedostossa on sekä 'routines' että 'routine'. Käytä vain toista.",
    );
  }
  if (!hasRoutines && !hasRoutine) {
    throw new RoutinePackImportError("Tiedostosta puuttuu 'routines' tai 'routine'.");
  }

  if (hasRoutines) {
    if (!Array.isArray(obj.routines)) {
      throw new RoutinePackImportError("'routines' ei ole lista.");
    }
    if (obj.routines.length > MAX_ROUTINES) {
      throw new RoutinePackImportError(
        `Liian monta rutiinia (yli ${MAX_ROUTINES}). Jaa tiedosto useampaan osaan.`,
      );
    }
    obj.routines.forEach((r, i) => validateRoutine(r, `routines[${i}]`));
    return { v: 1, routines: obj.routines as RoutineDraftWire[] };
  }

  validateRoutine(obj.routine, 'routine');
  return { v: 1, routine: obj.routine as RoutineDraftWire };
}

export function toRoutineDrafts(pack: RoutinePackV1): RoutineDraft[] {
  if ('routines' in pack) {
    return pack.routines.map((r) => ({
      name: r.name,
      emoji: r.emoji,
      tasks: r.tasks.map((t) => ({ name: t.name, emoji: t.emoji, tag: t.tag })),
    }));
  }
  return [
    {
      name: pack.routine.name,
      emoji: pack.routine.emoji,
      tasks: pack.routine.tasks.map((t) => ({ name: t.name, emoji: t.emoji, tag: t.tag })),
    },
  ];
}

function validateRoutine(value: unknown, path: string): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new RoutinePackImportError(`Rutiinin '${path}' muoto on virheellinen.`);
  }
  const r = value as Record<string, unknown>;
  if (typeof r.name !== 'string') {
    throw new RoutinePackImportError(`Rutiinin '${path}' nimi puuttuu tai on väärää tyyppiä.`);
  }
  if (typeof r.emoji !== 'string') {
    throw new RoutinePackImportError(`Rutiinin '${path}' emoji puuttuu tai on väärää tyyppiä.`);
  }
  if (!Array.isArray(r.tasks)) {
    throw new RoutinePackImportError(`Rutiinin '${path}' tasks-lista puuttuu.`);
  }
  r.tasks.forEach((t, i) => validateTask(t, `${path}.tasks[${i}]`));
}

function validateTask(value: unknown, path: string): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new RoutinePackImportError(`Tehtävän '${path}' muoto on virheellinen.`);
  }
  const t = value as Record<string, unknown>;
  if (typeof t.name !== 'string') {
    throw new RoutinePackImportError(`Tehtävän '${path}' nimi puuttuu tai on väärää tyyppiä.`);
  }
  if (typeof t.emoji !== 'string') {
    throw new RoutinePackImportError(`Tehtävän '${path}' emoji puuttuu tai on väärää tyyppiä.`);
  }
  if (!('tag' in t)) {
    throw new RoutinePackImportError(`Tehtävältä '${path}' puuttuu kenttä 'tag' (käytä null).`);
  }
  if (t.tag !== null && typeof t.tag !== 'string') {
    throw new RoutinePackImportError(`Tehtävän '${path}' 'tag' on väärää tyyppiä.`);
  }
}
